import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import { brandsQuery, purchaseOrdersQuery } from '@/api/queries';
import type { PurchaseOrderListParams, PurchaseOrderStatus } from '@/api/types';
import type { ToolConfig } from './utils/create-tool';
import { handlePoError } from './utils/po-error-handler';

const PO_STATUSES: [PurchaseOrderStatus, ...PurchaseOrderStatus[]] = [
  'open_draft',
  'submitted',
  'amended',
  'confirmed',
  'partially_confirmed',
  'pending_manager_approval',
  'rejected',
  'rejected_by_fulfillment',
  'cancelled',
  'shipped',
  'closed_received',
];

const inputSchema = z.object({
  status_filter: z.array(z.enum(PO_STATUSES)).optional(),
  brand_name: z.string().optional(),
  limit: z.number().optional().default(20),
});

async function execute(params: z.infer<typeof inputSchema>, queryClient: QueryClient) {
  try {
    const listParams: PurchaseOrderListParams = {
      limit: params.limit,
      sort_by: 'updated_at',
      sort_direction: 'desc',
    };

    if (params.status_filter?.length) {
      listParams.status_filter = params.status_filter;
    }

    // Resolve brand name → brandCatalogBrandId for filtering
    if (params.brand_name) {
      const brands = await queryClient.fetchQuery(brandsQuery());
      const needle = params.brand_name.toLowerCase();
      const match =
        brands.find((b) => b.brandName.toLowerCase() === needle) ??
        brands.find((b) => b.brandName.toLowerCase().includes(needle));

      if (match?.brandCatalogBrandId) {
        listParams.brand_ids = [match.brandCatalogBrandId];
      }
    }

    const { orders, totalCount } = await queryClient.fetchQuery(purchaseOrdersQuery(listParams));

    const purchase_orders = orders.map((o) => ({
      id: o.attributes.id,
      po_number: o.attributes.purchaseOrderNumber,
      title: o.attributes.title,
      brand_name: o.attributes.brandName,
      status: o.attributes.status,
      action_required_by: o.attributes.actionRequiredBy,
      item_count: o.attributes.items,
      requested_subtotal_cents: o.attributes.requestedSubtotalInCents,
      created_at: o.attributes.createdAt,
      updated_at: o.attributes.updatedAt,
      submitted_at: o.attributes.submittedAt,
      expected_arrival_date: o.attributes.expectedArrivalDate,
    }));

    return { purchase_orders, total_count: totalCount };
  } catch (error) {
    return handlePoError(error);
  }
}

export const listPurchaseOrdersTool: ToolConfig<typeof inputSchema> = {
  name: 'list_purchase_orders',
  description:
    "Lists purchase orders, optionally filtered by status or brand name. Use this to check what orders exist, what's been submitted, or what drafts are pending. Supports statuses: open_draft, submitted, amended, confirmed, partially_confirmed, rejected, cancelled, shipped, closed_received.",
  inputSchema,
  execute,
};
